import { useState, useEffect } from "react";
import { useMatchCards } from "./MatchCardsManager";
import { useMatchGoals } from "../goalsMatchs/MatchGoalsManager"; // Para fetchPlayersByTeam
import { showToast } from "../../ToastNotification";

const MatchCardsModal = ({ match, selectedSubcategory, onClose }) => {
  const { cards, addCard, deleteCard } = useMatchCards(match.id);
  const { players, fetchPlayersByTeam } = useMatchGoals(match.id);

  const [form, setForm] = useState({
    team_id: match.team1_id,
    player_id: "",
    card_type: "yellow",
    card_time_minute: 0,
    card_time_second: 0,
  });

  const [debouncedTeamId, setDebouncedTeamId] = useState(form.team_id);

  // Debounce para evitar fetchs rápidos
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTeamId(form.team_id);
    }, 300);
    return () => clearTimeout(handler);
  }, [form.team_id]);

  // Fetch jugadores cuando cambia el equipo debounced o la subcategoría
  useEffect(() => {
    if (!debouncedTeamId) return;
    const teamKey = debouncedTeamId === match.team1_id ? "team1" : "team2";
    if (!players.loading) {
      fetchPlayersByTeam(debouncedTeamId, teamKey, selectedSubcategory?.id);
    }
    setForm((prev) => ({ ...prev, player_id: "" }));
  }, [debouncedTeamId, selectedSubcategory?.id]);

  const currentPlayers =
    form.team_id === match.team1_id ? players.team1 : players.team2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.player_id) {
      showToast.error("Debes seleccionar un jugador");
      return;
    }
    try {
      await addCard({ match_id: match.id, ...form });
      setForm({
        team_id: match.team1_id,
        player_id: "",
        card_type: "yellow",
        card_time_minute: 0,
        card_time_second: 0,
      });
    } catch (err) {
      console.error("Error agregando tarjeta:", err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white text-black rounded-xl p-6 w-full max-w-lg shadow-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold mb-4">Gestionar Tarjetas</h2>

        {/* Lista de tarjetas */}
        <ul className="space-y-2 mb-4 max-h-40 overflow-y-auto">
          {cards.map((c) => (
            <li
              key={c.id}
              className="flex justify-between items-center bg-gray-100 p-2 rounded"
            >
              <span>
                {c.player_name} ({c.team_name}) —{" "}
                <span
                  className={
                    c.card_type === "yellow"
                      ? "text-yellow-600"
                      : "text-red-600"
                  }
                >
                  {c.card_type === "yellow" ? "Amarilla" : "Roja"}
                </span>{" "}
                {c.card_time_minute}'{c.card_time_second}"
              </span>
              <button
                onClick={() => deleteCard(c.id)}
                className="text-sm px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Eliminar
              </button>
            </li>
          ))}
        </ul>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Selección de equipo */}
          <select
            value={form.team_id}
            onChange={(e) => setForm({ ...form, team_id: e.target.value })}
            className="w-full border rounded p-2 bg-white text-black"
            required
          >
            <option value="">Equipo</option>
            <option value={match.team1_id}>
              {match.team1_name}
              {/* Mostrar categoría y grupo del equipo 1 */}
              {match.team1_category && (
                <span className="ml-2 text-sm text-gray-400">
                  ({match.team1_category})
                </span>
              )}
              {match.team1_group_name && (
                <span className="ml-2 text-sm text-gray-400">
                  - {match.team1_group_name}
                </span>
              )}
            </option>
            <option value={match.team2_id}>
              {match.team2_name}
              {/* Mostrar categoría y grupo del equipo 2 */}
              {match.team2_category && (
                <span className="ml-2 text-sm text-gray-400">
                  ({match.team2_category})
                </span>
              )}
              {match.team2_group_name && (
                <span className="ml-2 text-sm text-gray-400">
                  - {match.team2_group_name}
                </span>
              )}
            </option>
          </select>

          {/* Selección de jugador */}
          <select
            value={form.player_id}
            onChange={(e) => setForm({ ...form, player_id: e.target.value })}
            className="w-full border rounded p-2 text-black"
            required
            disabled={!form.team_id || players.loading}
          >
            <option value="">
              {players.loading
                ? "Cargando jugadores..."
                : "Seleccionar jugador"}
            </option>

            {currentPlayers.map((p) => (
              <option
                key={p.id}
                value={p.id}
                style={{ color: "black", backgroundColor: "white" }}
              >
                {`${p.first_name || ""} ${p.last_name || ""}`.trim()}
              </option>
            ))}
          </select>

          {/* Tipo de tarjeta */}
          <select
            value={form.card_type}
            onChange={(e) => setForm({ ...form, card_type: e.target.value })}
            className="w-full border rounded p-2 bg-white text-black"
          >
            <option value="yellow">Amarilla</option>
            <option value="red">Roja</option>
          </select>

          {/* Tiempo de la tarjeta */}
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Minuto"
              value={form.card_time_minute}
              onChange={(e) =>
                setForm({ ...form, card_time_minute: e.target.value })
              }
              className="flex-1 border rounded p-2 bg-white text-black placeholder-gray-500"
              required
            />
            <input
              type="number"
              placeholder="Segundo"
              value={form.card_time_second}
              onChange={(e) =>
                setForm({ ...form, card_time_second: e.target.value })
              }
              className="flex-1 border rounded p-2 bg-white text-black placeholder-gray-500"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-emerald-500 text-white py-2 rounded hover:bg-emerald-600"
          >
            Agregar Tarjeta
          </button>
        </form>

        <button
          onClick={onClose}
          className="mt-4 w-full bg-gray-300 text-black py-2 rounded hover:bg-gray-400"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};

export default MatchCardsModal;
